import { useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { GET_SOLICITUDES, APROBAR_SOLICITUD, RECHAZAR_SOLICITUD } from '../graphql/queries'
import './Solicitudes.css'

function Solicitudes() {
  const [filtroEstado, setFiltroEstado] = useState('pendiente')
  const [filtroInterfaz, setFiltroInterfaz] = useState('')
  const [showAprobarModal, setShowAprobarModal] = useState(false)
  const [showRechazarModal, setShowRechazarModal] = useState(false)
  const [solicitudSeleccionada, setSolicitudSeleccionada] = useState(null)
  const [codigoGenerado, setCodigoGenerado] = useState(null)
  const [motivoRechazo, setMotivoRechazo] = useState('')
  const [fechaVencimiento, setFechaVencimiento] = useState('')

  const { data, loading, refetch } = useQuery(GET_SOLICITUDES, {
    variables: {
      estado: filtroEstado || null,
      tipo_interfaz: filtroInterfaz || null
    }
  })

  const [aprobarSolicitud, { loading: aprobando }] = useMutation(APROBAR_SOLICITUD, {
    onCompleted: (data) => {
      setCodigoGenerado(data.aprobarSolicitud.codigo_licencia)
      refetch()
      // Cerrar modal automaticamente despues de 3 segundos
      setTimeout(() => {
        setShowAprobarModal(false)
        setSolicitudSeleccionada(null)
        setCodigoGenerado(null)
      }, 3000)
    }
  })

  const [rechazarSolicitud, { loading: rechazando }] = useMutation(RECHAZAR_SOLICITUD, {
    onCompleted: () => {
      setShowRechazarModal(false)
      setSolicitudSeleccionada(null)
      setMotivoRechazo('')
      refetch()
    }
  })

  const solicitudes = data?.solicitudes || []

  const handleAprobar = (solicitud) => {
    setSolicitudSeleccionada(solicitud)
    setCodigoGenerado(null)
    setFechaVencimiento('')
    setShowAprobarModal(true)
  }

  const handleRechazar = (solicitud) => {
    setSolicitudSeleccionada(solicitud)
    setMotivoRechazo('')
    setShowRechazarModal(true)
  }

  const confirmarAprobacion = () => {
    aprobarSolicitud({
      variables: {
        id: solicitudSeleccionada.id,
        fecha_vencimiento: fechaVencimiento || null
      }
    })
  }

  const confirmarRechazo = () => {
    if (!motivoRechazo.trim()) {
      alert('Debe ingresar un motivo de rechazo')
      return
    }
    rechazarSolicitud({
      variables: {
        id: solicitudSeleccionada.id,
        motivo: motivoRechazo
      }
    })
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    alert('Codigo copiado al portapapeles')
  }

  const getEstadoBadge = (estado) => {
    switch (estado) {
      case 'pendiente': return <span className="badge badge-warning">Pendiente</span>
      case 'aprobada': return <span className="badge badge-success">Aprobada</span>
      case 'rechazada': return <span className="badge badge-danger">Rechazada</span>
      default: return <span className="badge badge-gray">{estado}</span>
    }
  }

  const getTipoLicenciaNombre = (tipo) => {
    const nombres = {
      'compra': 'Compra (Permanente)',
      'renta_mensual': 'Renta Mensual (30 dias)',
      'renta_trimestral': 'Renta Trimestral (90 dias)',
      'renta_anual': 'Renta Anual (365 dias)',
      'prueba': 'Prueba (7 dias)'
    }
    return nombres[tipo] || tipo
  }

  return (
    <div className="solicitudes-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Solicitudes de Licencia</h1>
          <p className="page-subtitle">Gestiona las solicitudes entrantes de los comercios</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="filtros-bar">
        <div className="filtro-grupo">
          <label>Estado:</label>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="form-select"
          >
            <option value="">Todos</option>
            <option value="pendiente">Pendientes</option>
            <option value="aprobada">Aprobadas</option>
            <option value="rechazada">Rechazadas</option>
          </select>
        </div>
        <div className="filtro-grupo">
          <label>Interfaz:</label>
          <select
            value={filtroInterfaz}
            onChange={(e) => setFiltroInterfaz(e.target.value)}
            className="form-select"
          >
            <option value="">Todas</option>
            <option value="restaurante">Restaurante</option>
            <option value="retail">Retail</option>
            <option value="hotel">Hotel</option>
            <option value="servicios">Servicios</option>
            <option value="pos_rapido">POS Rapido</option>
          </select>
        </div>
        <button className="btn btn-secondary" onClick={() => refetch()}>
          Actualizar
        </button>
      </div>

      {/* Contenido */}
      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      ) : solicitudes.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>No hay solicitudes</h3>
          <p>
            {filtroEstado === 'pendiente'
              ? 'No hay solicitudes pendientes por procesar'
              : 'No se encontraron solicitudes con los filtros seleccionados'}
          </p>
        </div>
      ) : (
        <div className="solicitudes-grid">
          {solicitudes.map((solicitud) => (
            <div key={solicitud.id} className={`solicitud-card estado-${solicitud.estado}`}>
              <div className="solicitud-header">
                <div className="solicitud-comercio">
                  <h3>{solicitud.nombre_comercial}</h3>
                  <p className="nit">NIT: {solicitud.nit}</p>
                </div>
                {getEstadoBadge(solicitud.estado)}
              </div>

              <div className="solicitud-body">
                <div className="solicitud-info">
                  <div className="info-row">
                    <span className="label">Razon Social:</span>
                    <span className="value">{solicitud.razon_social}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Tipo Interfaz:</span>
                    <span className="value capitalize">{solicitud.tipo_interfaz}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Tipo Licencia:</span>
                    <span className="value">{getTipoLicenciaNombre(solicitud.tipo_licencia)}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Email:</span>
                    <span className="value">{solicitud.email}</span>
                  </div>
                  {solicitud.telefono && (
                    <div className="info-row">
                      <span className="label">Telefono:</span>
                      <span className="value">{solicitud.telefono}</span>
                    </div>
                  )}
                  {solicitud.ciudad && (
                    <div className="info-row">
                      <span className="label">Ciudad:</span>
                      <span className="value">{solicitud.ciudad}</span>
                    </div>
                  )}
                </div>

                <div className="solicitud-meta">
                  <span className="fecha">
                    {new Date(solicitud.created_at).toLocaleString('es-CO', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      timeZone: 'America/Bogota'
                    })}
                  </span>
                  {solicitud.ip_origen && (
                    <span className="ip">IP: {solicitud.ip_origen}</span>
                  )}
                </div>

                {solicitud.estado === 'rechazada' && solicitud.motivo_rechazo && (
                  <div className="motivo-rechazo">
                    <strong>Motivo:</strong> {solicitud.motivo_rechazo}
                  </div>
                )}
              </div>

              {solicitud.estado === 'pendiente' && (
                <div className="solicitud-actions">
                  <button
                    className="btn btn-success"
                    onClick={() => handleAprobar(solicitud)}
                  >
                    Aprobar
                  </button>
                  <button
                    className="btn btn-danger-outline"
                    onClick={() => handleRechazar(solicitud)}
                  >
                    Rechazar
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal Aprobar */}
      {showAprobarModal && solicitudSeleccionada && (
        <div className="modal-overlay" onClick={() => !codigoGenerado && setShowAprobarModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {codigoGenerado ? 'Licencia Generada' : 'Aprobar Solicitud'}
              </h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowAprobarModal(false)
                  setSolicitudSeleccionada(null)
                  setCodigoGenerado(null)
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {codigoGenerado ? (
                <div className="codigo-generado">
                  <div className="success-icon">✓</div>
                  <p>La solicitud ha sido aprobada y la licencia generada correctamente.</p>
                  <p className="small">El codigo se ha enviado al email: <strong>{solicitudSeleccionada.email}</strong></p>
                  <div className="codigo-box-grande">
                    <code>{codigoGenerado}</code>
                  </div>
                  <div className="codigo-actions">
                    <button
                      className="btn btn-primary"
                      onClick={() => copyToClipboard(codigoGenerado)}
                    >
                      Copiar Codigo
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowAprobarModal(false)
                        setSolicitudSeleccionada(null)
                        setCodigoGenerado(null)
                      }}
                    >
                      Cerrar
                    </button>
                  </div>
                  <p className="auto-close-msg">Este modal se cerrara automaticamente...</p>
                </div>
              ) : (
                <>
                  <div className="resumen-solicitud">
                    <h4>{solicitudSeleccionada.nombre_comercial}</h4>
                    <p>NIT: {solicitudSeleccionada.nit}</p>
                    <p>Tipo: {getTipoLicenciaNombre(solicitudSeleccionada.tipo_licencia)}</p>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Fecha de Vencimiento (opcional)</label>
                    <input
                      type="date"
                      className="form-input"
                      value={fechaVencimiento}
                      onChange={(e) => setFechaVencimiento(e.target.value)}
                    />
                    <small className="text-muted">
                      Si no se especifica, se calculara automaticamente segun el tipo de licencia
                    </small>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              {!codigoGenerado && (
                <>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowAprobarModal(false)
                      setSolicitudSeleccionada(null)
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={confirmarAprobacion}
                    disabled={aprobando}
                  >
                    {aprobando ? 'Aprobando...' : 'Aprobar y Generar Licencia'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Rechazar */}
      {showRechazarModal && solicitudSeleccionada && (
        <div className="modal-overlay" onClick={() => setShowRechazarModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Rechazar Solicitud</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowRechazarModal(false)
                  setSolicitudSeleccionada(null)
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="resumen-solicitud warning">
                <h4>{solicitudSeleccionada.nombre_comercial}</h4>
                <p>NIT: {solicitudSeleccionada.nit}</p>
              </div>

              <div className="form-group">
                <label className="form-label">Motivo del Rechazo *</label>
                <textarea
                  className="form-textarea"
                  rows="3"
                  value={motivoRechazo}
                  onChange={(e) => setMotivoRechazo(e.target.value)}
                  placeholder="Ingrese el motivo del rechazo..."
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowRechazarModal(false)
                  setSolicitudSeleccionada(null)
                }}
              >
                Cancelar
              </button>
              <button
                className="btn btn-danger"
                onClick={confirmarRechazo}
                disabled={rechazando || !motivoRechazo.trim()}
              >
                {rechazando ? 'Rechazando...' : 'Confirmar Rechazo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Solicitudes
